import { useMemo } from 'react';

import {
  accessibleSurfacesForRole,
  roleCan,
  type DashboardAction,
  type DashboardSurface,
} from '@/lib/dashboard-permissions';

import { useCurrentTenantRole } from './useCurrentTenantRole';

import type { MemberRole } from '@/__generated__/graphql';

export interface DashboardPermissions {
  /** The caller's role in the active workspace, or `null` while it loads. */
  readonly role: MemberRole | null;
  /** True once the role is known (i.e. not still loading). */
  readonly roleKnown: boolean;
  /**
   * Whether the caller may perform `action` on `surface`.
   *
   * Fails SAFE while the role is still loading: returns `true` (allow), so an
   * ALLOWED user never sees a flash of a hidden/disabled control before the role
   * resolves (the `BillingPage` / `ChannelBCard` pattern). It returns `false`
   * ONLY for a KNOWN role that the server would reject — so `!can(...)` reads
   * cleanly as "known-denied", and the gate never hides a control from someone
   * who can use it.
   */
  readonly can: (surface: DashboardSurface, action: DashboardAction) => boolean;
  /**
   * The surfaces the caller can at least view (drives nav visibility), or `null`
   * while the role loads — callers treat `null` as "show everything" so a nav row
   * is never hidden from a user who can reach it.
   */
  readonly accessibleSurfaces: () => DashboardSurface[] | null;
}

/**
 * The caller's dashboard permissions, derived from `useCurrentTenantRole()` and
 * the client mirror of the server RBAC matrix (`@/lib/dashboard-permissions`).
 *
 * The systemic form of the one-off owner gate `BillingPage` does inline: any
 * surface can call `usePermissions().can('catalog', 'edit')` to hide/disable a
 * control the server would reject, instead of rendering a dead-end button that
 * only fails on click. The server remains the sole authority; this avoids the
 * dishonest enabled-then-fails control.
 */
export function usePermissions(): DashboardPermissions {
  const role = useCurrentTenantRole();
  return useMemo<DashboardPermissions>(
    () => ({
      role,
      roleKnown: role !== null,
      can: (surface, action) => (role === null ? true : roleCan(role, surface, action)),
      accessibleSurfaces: () => (role === null ? null : accessibleSurfacesForRole(role)),
    }),
    [role],
  );
}
